import { ScanErrorKind, isTrivia, type Token, TokenKind } from '../../json/tokens';
import { desyncOffset } from '../support';
import type { RepairContext, RepairProposal, RepairRule } from '../types';

/**
 * Close a string literal the document ran out of: `{"a":"unclosed` → `{"a":"unclosed"}`
 * (with `close-open-containers` supplying the brace).
 *
 * Fires in exactly one place: at the end of the document, where the literal ran out because
 * the document did. That is the truncated-log case, and it is the only one where the end of
 * the string is not a guess.
 *
 * A string broken by a raw line break in the MIDDLE of a document is deliberately left
 * alone. `{"a":"x<LF>y"}` scans as two unterminated strings, and closing both produces
 * `{"a":"x"<LF>y"}"` — no worse than the input, but no better either, and it hides the fact
 * that the author probably meant the line break to be data (which wants `\n`, not a quote).
 * Lint reports it; repair does not guess.
 *
 * The legacy pipeline had no rule for this at all and reported `no-repair` (recon B33).
 *
 * A literal ending in an odd number of backslashes gets `\"` rather than `"`: appending a
 * bare quote to `{"a":"x\` would produce `"x\"`, which is still unterminated. The backslash
 * is the user's data and is kept.
 */
export const closeUnterminatedStringRule: RepairRule = {
  id: 'close-unterminated-string',
  confidence: 'safe',
  priority: 40,
  resolves: ['string/unterminated'],

  detect(ctx: RepairContext): readonly RepairProposal[] {
    // A document with a raw line break inside a string ALSO ends in an unterminated string —
    // `{"a":"x⏎y"}` closes with `String("}`) — but that trailing fragment is an artefact of
    // the break, not a truncated log. Closing it writes `{"a":"x⏎y"}"`. The restraint this
    // rule's header promises has to cover the whole document, not just the token in hand.
    if (desyncOffset(ctx.tokens) !== undefined) return [];

    const proposals: RepairProposal[] = [];

    for (let i = 0; i < ctx.tokens.length; i += 1) {
      const token = ctx.tokens[i];
      if (token === undefined || token.kind !== TokenKind.String) continue;
      if (token.scanError !== ScanErrorKind.UnterminatedString) continue;
      if (!onlyTriviaFollows(ctx.tokens, i)) continue;

      const insert = trailingBackslashes(ctx.source, token) % 2 === 1 ? '\\"' : '"';

      proposals.push({
        ruleId: 'close-unterminated-string',
        span: { from: token.from, to: token.to },
        edits: [{ from: token.to, to: token.to, insert }],
        labelCode: 'fix.close-unterminated-string',
        resolves: 'string/unterminated',
      });
    }

    return proposals;
  },
};

/** True when nothing but trivia stands between the token and the end of the document. */
function onlyTriviaFollows(tokens: readonly Token[], index: number): boolean {
  for (let i = index + 1; i < tokens.length; i += 1) {
    const token = tokens[i];
    if (token === undefined) continue;
    if (token.kind === TokenKind.EOF) return true;
    if (!isTrivia(token)) return false;
  }
  return true;
}

function trailingBackslashes(source: string, token: Token): number {
  let count = 0;
  for (let at = token.to - 1; at > token.from && source.charAt(at) === '\\'; at -= 1) count += 1;
  return count;
}
